import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Empty,
  Image,
  Row,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  AlertOutlined,
  BlockOutlined,
  CameraOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";

const { Text } = Typography;

type SourceType = "plot" | "control_point" | "adjacent_crop" | "unknown";

interface PhotoRow {
  id: string;
  image_url: string | null;
  description: string;
  uploaded_by_name: string | null;
  created: string | null;
  source_type: SourceType;
  source_id: string | number | null;
  source_name: string;
}

interface PhotosResponse {
  photos: PhotoRow[];
}

interface AuditPhotoGalleryProps {
  auditId: number;
  /** Opens the parent-owned `PhotoUploadModal`. Disabled state is
   *  decided by the parent (e.g. when the audit has no plots yet). */
  onUploadClick?: () => void;
  /** When true, the "Subir fotos" button is disabled. Used by the
   *  parent to gate uploads until at least one plot exists. */
  uploadDisabled?: boolean;
  /** Bump to force a re-fetch — used by the parent after a
   *  successful upload to surface the new photos. */
  refreshKey?: number;
  /** Fires with the total photo count whenever the data loads —
   *  used by the parent to render a tab badge. */
  onCountChange?: (count: number) => void;
}

/**
 * Unified photo gallery for the audit detail. Aggregates every photo
 * uploaded under the audit — regardless of whether it was attached to
 * a plot, a control-point review, or an adjacent-crop review — and
 * shows them in a single responsive grid with per-source filters.
 *
 * Each thumbnail carries a coloured chip with the source type so the
 * auditor can tell at a glance where the photo belongs.
 */
export const AuditPhotoGallery = ({
  auditId,
  onUploadClick,
  uploadDisabled = false,
  refreshKey = 0,
  onCountChange,
}: AuditPhotoGalleryProps) => {
  const { t } = useTranslation();
  const [data, setData] = useState<PhotoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | SourceType>("all");

  const fetchData = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    try {
      const res = await apiClient.get<PhotosResponse>(
        `/audits/${auditId}/photos/`,
      );
      const items = res.data?.photos ?? [];
      setData(items);
      onCountChange?.(items.length);
    } catch {
      message.error(
        t("audit.photos.load_error", "Error al cargar las fotografías"),
      );
      setData([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [auditId, t, onCountChange]);

  useEffect(() => {
    fetchData();
    // `refreshKey` lets the parent trigger a re-fetch after upload.
  }, [fetchData, refreshKey]);

  // Visible photos honour the segmented filter.
  const visible = useMemo(
    () => (filter === "all" ? data : data.filter((p) => p.source_type === filter)),
    [data, filter],
  );

  // Per-source counts shown next to the filter labels.
  const counts = useMemo(() => {
    const c = { all: data.length, plot: 0, control_point: 0, adjacent_crop: 0 };
    for (const p of data) {
      if (p.source_type === "plot") c.plot++;
      else if (p.source_type === "control_point") c.control_point++;
      else if (p.source_type === "adjacent_crop") c.adjacent_crop++;
    }
    return c;
  }, [data]);

  const sourceConfig: Record<
    SourceType,
    { color: string; icon: React.ReactNode; label: string }
  > = {
    plot: {
      color: "orange",
      icon: <BlockOutlined />,
      label: t("audit.photos.plot", "Parcela"),
    },
    control_point: {
      color: "red",
      icon: <AlertOutlined />,
      label: t("audit.photos.control_point", "Punto de control"),
    },
    adjacent_crop: {
      color: "purple",
      icon: <EnvironmentOutlined />,
      label: t("audit.photos.adjacent_crop", "Cultivo colindante"),
    },
    unknown: {
      color: "default",
      icon: <CameraOutlined />,
      label: t("audit.photos.unknown", "Sin origen"),
    },
  };

  const renderCard = (photo: PhotoRow) => {
    const cfg = sourceConfig[photo.source_type] ?? sourceConfig.unknown;
    return (
      <Col key={photo.id} xs={24} sm={12} md={8} lg={8} xl={6}>
        <Card
          hoverable
          size="small"
          styles={{ body: { padding: 12 } }}
          cover={
            <div
              style={{
                height: 180,
                background: "#f5f5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {photo.image_url ? (
                // antd Image gives us the lightbox/preview viewer for
                // free; one click opens the full image.
                <Image
                  src={photo.image_url}
                  alt={photo.description || photo.source_name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  preview={{ mask: t("audit.photos.preview", "Ver") }}
                />
              ) : (
                <CameraOutlined style={{ fontSize: 32, color: "#bfbfbf" }} />
              )}
            </div>
          }
        >
          {/* Source chip — coloured per entity type */}
          <div style={{ marginBottom: 6 }}>
            <Tag color={cfg.color} icon={cfg.icon} style={{ margin: 0 }}>
              {cfg.label}
              {photo.source_name ? ` · ${photo.source_name}` : ""}
            </Tag>
          </div>

          {/* Description (optional) */}
          {photo.description && (
            <Text
              type="secondary"
              style={{
                fontSize: 12,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={photo.description}
            >
              {photo.description}
            </Text>
          )}

          {/* Uploader + date */}
          <div
            style={{
              marginTop: 6,
              fontSize: 11,
              color: "#8c8c8c",
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
            }}
          >
            {photo.uploaded_by_name && (
              <span>
                <UserOutlined style={{ marginRight: 4 }} />
                {photo.uploaded_by_name}
              </span>
            )}
            {photo.created && (
              <span>{dayjs(photo.created).format("DD/MM/YYYY")}</span>
            )}
          </div>
        </Card>
      </Col>
    );
  };

  return (
    <Card
      title={
        <Space>
          <CameraOutlined />
          {t("audit.photos.title", "Galería de fotos")}
          <Tag color="default">{data.length}</Tag>
        </Space>
      }
      bordered={false}
      style={{ marginBottom: 0 }}
      extra={
        <Space wrap>
          <Segmented
            value={filter}
            onChange={(v) => setFilter(v as "all" | SourceType)}
            options={[
              {
                label: `${t("audit.photos.all", "Todas")} (${counts.all})`,
                value: "all",
              },
              {
                label: (
                  <Space size={4}>
                    <BlockOutlined />
                    {t("audit.photos.plots_short", "Parcelas")} ({counts.plot})
                  </Space>
                ),
                value: "plot",
                disabled: counts.plot === 0,
              },
              {
                label: (
                  <Space size={4}>
                    <AlertOutlined />
                    {t("audit.photos.cps_short", "PC")} ({counts.control_point})
                  </Space>
                ),
                value: "control_point",
                disabled: counts.control_point === 0,
              },
              {
                label: (
                  <Space size={4}>
                    <EnvironmentOutlined />
                    {t("audit.photos.acs_short", "Var.")} ({counts.adjacent_crop})
                  </Space>
                ),
                value: "adjacent_crop",
                disabled: counts.adjacent_crop === 0,
              },
            ]}
          />
          {onUploadClick && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onUploadClick}
              disabled={uploadDisabled}
              title={
                uploadDisabled
                  ? t(
                      "audit.photos.upload_disabled_hint",
                      "Crea al menos una parcela para poder subir fotos",
                    )
                  : undefined
              }
            >
              {t("audit.photos.upload", "Subir fotos")}
            </Button>
          )}
        </Space>
      }
    >
      {loading && data.length === 0 ? (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin />
        </div>
      ) : visible.length === 0 ? (
        <Empty
          description={
            data.length === 0
              ? t(
                  "audit.photos.empty",
                  "Esta auditoría aún no tiene fotografías",
                )
              : t(
                  "audit.photos.empty_filter",
                  "No hay fotografías en esta categoría",
                )
          }
        >
          {data.length === 0 && onUploadClick && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onUploadClick}
              disabled={uploadDisabled}
            >
              {t("audit.photos.upload_first", "Subir las primeras fotos")}
            </Button>
          )}
        </Empty>
      ) : (
        // Wrap the previewable images in a group so navigation arrows
        // (◀ ▶) work between thumbnails in the lightbox.
        <Image.PreviewGroup>
          <Row gutter={[16, 16]}>{visible.map(renderCard)}</Row>
        </Image.PreviewGroup>
      )}
    </Card>
  );
};

export default AuditPhotoGallery;
